import { create } from 'zustand';
import useLoadingStore from './use-loading';
import { makeRequest } from 'api/api-client';
import { HTTP } from 'constants/http-method';
import { API_ENDPOINTS } from 'api/endpoints';

const useRoleStore = create((set) => ({
  rolesLoading: false,
  totalRoles: 0,
  roles: [],
  pagination: {
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
  },

  // Function to reset currentPage only when an action happens
  resetCurrentPage: () => {
    set((state) => ({
      pagination: {
        ...state.pagination,
        currentPage: 1,
      },
    }));
  },

  // Function to get roles
  getRole: async ({ query = null, successCallback }) => {
    set({ rolesLoading: true });
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.GET,
        API_ENDPOINTS.GET_ROLE,
        null,
        query
      );

      if (response.success) {
        const rolesWithPermissions =
          response.data?.data?.map((role) => ({
            ...role,
            permissions:
              role.rolePermissions?.map(
                (permission) => permission?.permissionId
              ) || [],
          })) || [];
        set({
          totalRoles: response.data?.pagination?.totalRoles || 0,
          roles: rolesWithPermissions,
          pagination: {
            pageSize: response.data?.pagination?.limit,
            totalItems: response.data?.pagination?.total,
            currentPage: response.data?.pagination?.page,
            totalPages: response.data?.pagination?.totalpages,
          },
        });
        successCallback && successCallback();
      }
    } catch (error) {
    } finally {
      set({ rolesLoading: false });
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to create the roles
  createRole: async ({ data, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.POST,
        API_ENDPOINTS.CREATE_ROLE,
        data
      );

      if (response.success) {
        await useRoleStore.getState().getRole({
          successCallback: () => {
            useRoleStore.getState().resetCurrentPage(); // Reset page
            successCallback && successCallback(response);
          },
        });
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to delete roles
  deleteRole: async ({ id, successCallback, errCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.DELETE,
        API_ENDPOINTS.DELETE_ROLE,
        null,
        null,
        id
      );

      if (response.success) {
        set((state) => {
          const updatedRoles = state.roles.filter((role) => role.id !== id);
          const updatedTotalItems = updatedRoles.length;
          const updatedTotalPages = Math.ceil(
            updatedTotalItems / state.pagination.pageSize
          );

          return {
            roles: updatedRoles,
            pagination: {
              ...state.pagination,
              totalItems: updatedTotalItems,
              totalPages: updatedTotalPages,
            },
          };
        });
        successCallback && successCallback(response);
      }
    } catch (error) {
      errCallback && errCallback();
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to update roles
  updateRole: async ({ id, data, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.PUT,
        API_ENDPOINTS.UPDATE_ROLE,
        data,
        null,
        id
      );

      if (response.success) {
        set((state) => {
          // Find the updated role
          const updatedRole = state.roles.find((role) => role.id === id);

          // If the role is found, update it and move it to the first position
          if (updatedRole) {
            const updatedRoles = [
              {
                ...updatedRole,
                userRole: data?.name,
                description: data?.description,
                permissions: data?.permissions,
                ...response.data?.data, // Include any additional data from the response
              }, // Updated role
              ...state.roles.filter((role) => role.id !== id), // Rest of the roles
            ];

            return {
              roles: updatedRoles,
              pagination: {
                ...state.pagination,
                currentPage: 1,
              },
            };
          }

          // If the role is not found, return the state as is
          return state;
        });

        successCallback && successCallback(response);
      }
    } catch (error) {
      // Handle error
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },
}));

export default useRoleStore;
